import type { AdminCrewReportOwnerRepatriationChartProps } from "../../models/admin-crew-report.models";
import {
  getAdminCrewReportOwnerRepatriationAxisMaximum,
  getAdminCrewReportOwnerRepatriationAxisTicks,
} from "../../utils/admin-crew-report-statistics.utils";
import {
  ADMIN_CREW_REPORT_OWNER_REPATRIATION_SEGMENTS,
  ADMIN_CREW_REPORT_STATISTICS_TITLE_CLASS_NAME,
  ADMIN_CREW_REPORT_STATISTICS_VIEW_LABELS,
} from "../../utils/admin-crew-report.constants";
import AdminCrewReportOwnerRepatriationBarRow from "./AdminCrewReportOwnerRepatriationBarRow";
import type { CSSProperties } from "react";

const AdminCrewReportOwnerRepatriationChart = ({ statistics, view }: AdminCrewReportOwnerRepatriationChartProps) => {
  const viewLabels = ADMIN_CREW_REPORT_STATISTICS_VIEW_LABELS[view];
  const signOnDateSubject = view === "women" ? "women's" : "crew";
  const visibleOwners = statistics.owners.filter((owner) => owner.totalVesselCount > 0);
  const axisMaximum = getAdminCrewReportOwnerRepatriationAxisMaximum(visibleOwners);
  const axisTicks = getAdminCrewReportOwnerRepatriationAxisTicks(axisMaximum);
  const exclusionMessage = `${statistics.excludedVesselCount} ${
    statistics.excludedVesselCount === 1 ? "vessel was" : "vessels were"
  } excluded because one or more ${signOnDateSubject} sign-on dates are missing.`;

  return (
    <div
      data-testid="admin-crew-report-owner-repatriation-layout"
      className="min-w-0 md:col-span-2 xl:relative xl:col-span-1 xl:col-start-1 xl:row-span-2 xl:row-start-2"
    >
      <article
        data-test="admin-crew-report-owner-repatriation-card"
        data-testid="admin-crew-report-owner-repatriation-card"
        className="flex h-[516px] min-w-0 flex-col self-stretch border border-[#D8E6E3] bg-white px-3 pb-2 pt-[11px] dark:border-gray-700 dark:bg-gray-800 xl:absolute xl:inset-0 xl:h-auto xl:min-h-0"
      >
        <h2 className={ADMIN_CREW_REPORT_STATISTICS_TITLE_CLASS_NAME}>{viewLabels.ownerRepatriationTitle}</h2>

        <ul
          aria-label={`${viewLabels.repatriationTitle} categories`}
          className="mt-3 flex flex-wrap justify-center gap-x-3 gap-y-1"
        >
          {ADMIN_CREW_REPORT_OWNER_REPATRIATION_SEGMENTS.map((segment) => (
            <li
              key={segment.key}
              className="flex items-center gap-1 whitespace-nowrap text-[10px] text-[#475351] dark:text-gray-300 sm:text-[11px]"
            >
              <span
                data-testid={`admin-crew-report-owner-repatriation-legend-${segment.key}`}
                className="h-2 w-2 flex-none rounded-full bg-[var(--admin-crew-report-segment-color)] dark:bg-[var(--admin-crew-report-segment-dark-color)]"
                style={
                  {
                    "--admin-crew-report-segment-color": segment.color,
                    "--admin-crew-report-segment-dark-color": segment.darkColor,
                  } as CSSProperties
                }
              />
              {segment.label}
            </li>
          ))}
        </ul>

        {visibleOwners.length === 0 ? (
          <div className="flex min-h-0 flex-1 items-center justify-center px-4 text-center text-sm text-[#65706F] dark:text-gray-300">
            No vessels with complete {signOnDateSubject} sign-on dates are available by owner.
          </div>
        ) : (
          <div className="mt-3 flex min-h-0 flex-1 flex-col">
            <div
              role="region"
              aria-label={`Scrollable ${viewLabels.ownerRepatriationTitle.toLowerCase()} chart rows`}
              data-test="admin-crew-report-owner-repatriation-scroll"
              data-testid="admin-crew-report-owner-repatriation-scroll"
              tabIndex={0}
              className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden pr-1 [scrollbar-gutter:stable] [scrollbar-width:thin] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#367C2B] [&::-webkit-scrollbar]:w-1.5"
            >
              {visibleOwners.map((owner) => (
                <AdminCrewReportOwnerRepatriationBarRow
                  key={owner.ownerName}
                  owner={owner}
                  axisMaximum={axisMaximum}
                  axisTicks={axisTicks}
                />
              ))}
            </div>

            <div
              aria-hidden="true"
              data-testid="admin-crew-report-owner-repatriation-axis"
              className="mt-1 h-6 flex-none overflow-y-auto overflow-x-hidden pr-1 [scrollbar-gutter:stable] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5"
            >
              <div className="grid h-full grid-cols-[minmax(7rem,42%)_minmax(0,1fr)]">
                <span />
                <div className="relative border-t border-[#8AA6A1] dark:border-gray-500">
                  {axisTicks.map((tick) => (
                    <span
                      key={tick}
                      data-testid={`admin-crew-report-owner-repatriation-axis-tick-${tick}`}
                      className="absolute top-1 -translate-x-1/2 text-[10px] tabular-nums text-[#475351] dark:text-gray-300"
                      style={{ left: `${(tick / axisMaximum) * 100}%` }}
                    >
                      {tick}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {statistics.excludedVesselCount > 0 && (
          <p
            data-test="admin-crew-report-owner-repatriation-excluded"
            className="mt-1 flex-none text-[10px] leading-3 text-[#65706F] dark:text-gray-300"
          >
            {exclusionMessage}
          </p>
        )}
      </article>
    </div>
  );
};

export default AdminCrewReportOwnerRepatriationChart;
